import { useEffect, useState } from 'react';
import { CLIPS, REST_SRC, SHEET_SRC } from './clips';

/* One atlas image for every Buffy sheet (2026-09-29 smoothness fix).

   Why: a sprite that switches its background from one sheet PNG to another
   can show a blank frame while the browser decodes the new sheet (the frame
   audit caught one at the first jump; slower machines show one at the first
   use of every sheet). Here every sheet is drawn once into one canvas, one
   row per sheet, and the result is decoded BEFORE Buffy plays. After that a
   clip change only moves the strip to another row: no new image, no decode,
   no flash.

   Row 0 is the rest pose (rest.png); then SHEET_SRC in registry order. A clip
   uses its sheet's row (connect-quick shares connect's row). Pixels are
   copied 1:1 (no smoothing); the browser scales with image-rendering:
   pixelated at an integer scale.

   loadAtlas() is memoized; useAtlas() re-renders once when it is ready.
   Without a canvas (tests, very old browsers) it resolves to null and the
   players fall back to the per-sheet images. */

const CELL = 64;
let promise = null;
let atlas = null;
let keepAlive = null;

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export function loadAtlas() {
  if (promise) return promise;
  promise = (async () => {
    if (typeof document === 'undefined') return null;
    const names = ['rest', ...Object.keys(SHEET_SRC)];
    const srcs = [REST_SRC, ...Object.values(SHEET_SRC)];
    const imgs = await Promise.all(srcs.map(loadImage));
    const cols = Math.max(...imgs.map((im) => Math.round(im.naturalWidth / CELL)));
    const canvas = document.createElement('canvas');
    canvas.width = cols * CELL;
    canvas.height = imgs.length * CELL;
    const ctx = canvas.getContext && canvas.getContext('2d');
    if (!ctx) return null;
    ctx.imageSmoothingEnabled = false;
    const rowOf = {};
    imgs.forEach((im, row) => {
      ctx.drawImage(im, 0, row * CELL);
      rowOf[names[row]] = row;
    });
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) return null;
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.src = url;
    if (img.decode) await img.decode();
    else await new Promise((resolve) => { img.onload = resolve; });
    keepAlive = img; // hold the decoded bitmap
    atlas = { url, cols, rows: imgs.length, rowOf, img: keepAlive };
    return atlas;
  })().catch(() => null);
  return promise;
}

export const atlasNow = () => atlas;

/* The atlas row of a clip (its sheet's row). */
export const rowOfClip = (a, clipName) => {
  if (!a) return 0;
  const clip = CLIPS[clipName];
  const sheet = clip ? clip.sheet : 'rest';
  return a.rowOf[sheet] ?? 0;
};

export function useAtlas() {
  const [value, setValue] = useState(atlas);
  useEffect(() => {
    let live = true;
    if (!atlas) loadAtlas().then((a) => { if (live) setValue(a || false); });
    return () => { live = false; };
  }, []);
  return value;
}
